@file:OptIn(ExperimentalTime::class)

package com.macrofocus.high_d.axis.hierarchy

import com.macrofocus.common.crossplatform.CPHelper
import com.macrofocus.hierarchy.Hierarchy
import com.macrofocus.hierarchy.MutableHierarchy
import com.macrofocus.hierarchy.SimpleHierarchy
import com.macrofocus.high_d.axis.AxisFactory
import com.macrofocus.high_d.axis.AxisListener
import com.macrofocus.high_d.axis.AxisModel
import com.macrofocus.high_d.axis.ClonedAxisModel
import com.macrofocus.high_d.axis.group.AxisGroupModel
import com.macrofocus.high_d.axis.group.DefaultAxisGroupModel
import com.macrofocus.high_d.axis.group.FlatAxisGroupModel
import org.molap.dataframe.DataFrame
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

class DefaultAxisHierarchy<R, C>(axisFactory: AxisFactory<R, C>, columns: Iterable<C>?) : AbstractAxisHierarchy<R, C>(),
    MutableAxisHierarchy<R, C> {
    private val groupHierarchy: MutableHierarchy<AxisGroupModel<R, C>> = SimpleHierarchy<AxisGroupModel<R, C>>(DefaultAxisGroupModel("Root"))
    private val axisHierarchy: MutableMap<AxisModel<R,C>, AxisGroupModel<R,C>> = HashMap<AxisModel<R,C>, AxisGroupModel<R,C>>()
    override val axisFactory: AxisFactory<R, C> = axisFactory
    private val axisModels: MutableMap<C, AxisModel<R, C>>
    private val flatAxisGroupModel: AxisGroupModel<R, C> = FlatAxisGroupModel(this)

    constructor(axisFactory: AxisFactory<R, C>, dataFrame: DataFrame<R, C, *>, numerical: Boolean, maxVisible: Int) : this(
        axisFactory,
        null
    ) {
        val root: AxisGroupModel<R, C> = axisGroupHierarchy.root
        var count = 0
        for (column in dataFrame.columns()) {
            val axisModel: AxisModel<R, C>? = getAxisModel(column)
            if(axisModel != null) {
                if (count < maxVisible) {
                    if (numerical && CPHelper.instance.isNumericType(axisModel.type) && !axisModel.scaledInterval.isDegenerate) {
                        count++
                    } else if (!numerical && CPHelper.instance.isAssignableFrom(String::class, axisModel.type)) {
                        count++
                    } else if (numerical && CPHelper.instance.isAssignableFrom(Instant::class, axisModel.type) && !axisModel.interval.isDegenerate) {
                        count++
                    } else {
                        root.axisOrder!!.setVisible(axisModel, false)
                    }
                } else {
                    root.axisOrder!!.setVisible(axisModel, false)
                }
            }
        }
    }

    constructor(
        axisFactory: AxisFactory<R, C>,
        dataFrame: DataFrame<R, C, *>,
        numerical: Boolean,
        categorical: Boolean,
        maxVisible: Int
    ) : this(axisFactory, null) {
        val root: AxisGroupModel<R,C> = axisGroupHierarchy.root
        var count = 0
        for (column in dataFrame.columns()) {
            val axisModel: AxisModel<R, C>? = getAxisModel(column)
            if (count < maxVisible && axisModel != null) {
                if (numerical && CPHelper.instance.isNumericType(axisModel.type) && !axisModel.scaledInterval.isDegenerate) {
                    count++
                } else if (categorical && CPHelper.instance.isAssignableFrom(String::class, axisModel.type)) {
                    count++
                } else if (numerical && CPHelper.instance.isAssignableFrom(Instant::class, axisModel.type) && !axisModel.interval.isDegenerate) {
                    count++
                } else {
                    root.axisOrder!!.setVisible(axisModel, false)
                }
            } else {
                axisModel?.let { root.axisOrder!!.setVisible(it, false) }
            }
        }
    }

    override fun createClonedAxisModel(c: C): AxisModel<R, C> {
        return ClonedAxisModel(axisFactory.getAxisModel(c)!!)
    }

    /**
     * {@inheritDoc}
     */
    override fun getAxisModel(c: C): AxisModel<R,C>? {
        return if (!axisModels.containsKey(c)) {
            val axisModel: AxisModel<R,C>? = axisFactory.getAxisModel(c)
            if (axisModel != null) {
                axisModels[c] = axisModel
                axisHierarchy[axisModel] = axisGroupHierarchy.root
                axisGroupHierarchy.root.add(axisModel)
                axisModel
            } else {
                null
            }
        } else {
            axisModels[c]
        }
    }

    val root: AxisGroupModel<R, C>
        get() = groupHierarchy.root
    override val flat: AxisGroupModel<R, C>
        get() = flatAxisGroupModel

    override fun put(axisModel: AxisModel<R,C>, group: AxisGroupModel<R,C>) {
        axisHierarchy[axisModel] = group
    }

    private fun setGroupContent(axisGroup: AxisGroupModel<R,C>, vararg content: AxisModel<R,C>) {
//        List<AxisModel> toRemove = new ArrayList<AxisModel>();
//        for (AxisModel groupContent : axisGroup.getAxisModels()) {
//            toRemove.add(groupContent);
//        }
//
//        for (AxisModel groupContent : toRemove) {
//            removeFromGroup(axisGroup, groupContent);
//        }
        for (groupContent in content) {
            addToGroup(axisGroup, groupContent)
        }
    }

    override fun addToGroup(axisGroup: AxisGroupModel<R,C>, content: AxisModel<R,C>) {
//        if(getAxisGroup(content) != null) {
//            removeFromGroup(getAxisGroup(content), content);
//        }
        axisGroup.add(content)
        axisHierarchy[content] = axisGroup
    }

    override fun moveToGroup(from: AxisGroupModel<R,C>, to: AxisGroupModel<R,C>, content: AxisModel<R,C>) {
        removeFromGroup(from, content)
        addToGroup(to, content)
    }

    override fun removeFromGroup(axisGroup: AxisGroupModel<R,C>, content: AxisModel<R,C>) {
        axisGroup.remove(content)
        axisHierarchy.remove(content)
    }

    /**
     * {@inheritDoc}
     */
    override fun getAxisGroup(axisModel: AxisModel<R,C>): AxisGroupModel<R,C>? {
        return axisHierarchy[axisModel]
    }

    /**
     * {@inheritDoc}
     */
    override val axisGroupHierarchy: Hierarchy<AxisGroupModel<R, C>>
        get() = groupHierarchy

    /**
     * {@inheritDoc}
     */
    override fun createGroup(parent: AxisGroupModel<R,C>, name: String, vararg axisModels: AxisModel<R,C>): AxisGroupModel<R,C> {
        val axisGroup: AxisGroupModel<R,C> = DefaultAxisGroupModel(name)

        // This is currently the way to forward collapsed events
        axisGroup.addAxisListener(object : AxisListener {
            override fun axisChanged() {
                groupHierarchy.notifyHierarchyNodeChanged(axisGroup, parent, groupHierarchy.getIndexOfChild(parent, parent), false)
            }
        })
        groupHierarchy.addChild(parent, axisGroup)
        setGroupContent(axisGroup, *axisModels)
        return axisGroup
    }

    init {
        axisModels = HashMap<C, AxisModel<R, C>>()
        if (columns != null) {
            for (column in columns) {
                getAxisModel(column)
            }
        }
    }
}